<script setup lang="ts">
import { type ExecutionRow } from '@/api/patient-monthly-metrics';
import { getCareerColor } from '@/utils/careers';

defineProps<{
  rows: ExecutionRow[];
}>();

const PERCENT = 100;
const SUCCESS_THRESHOLD = 95;
const WARNING_THRESHOLD = 80;
const ROUND_FACTOR = 10;

function percentDone(row: ExecutionRow): number {
  if (row.planned === 0) return 0;

  const ratio = (row.actual / row.planned) * PERCENT;

  return Math.round(ratio * ROUND_FACTOR) / ROUND_FACTOR;
}

function progressColor(row: ExecutionRow): string {
  const percent = percentDone(row);
  if (percent >= SUCCESS_THRESHOLD) return 'success';
  if (percent >= WARNING_THRESHOLD) return 'warning';

  return 'error';
}
</script>

<template>
  <v-card class="pa-4 h-100 d-flex flex-column">
    <div class="d-flex align-center justify-space-between flex-wrap ga-1 mb-1">
      <span class="text-subtitle-1 font-weight-bold">Ejecución y Adhesión</span>
      <v-chip
        size="small"
        variant="tonal"
        label
      >
        Agendado vs. Realizado
      </v-chip>
    </div>
    <p class="text-caption text-medium-emphasis mb-3">
      Mapea el porcentaje de atenciones efectivamente brindadas y trazadas digitalmente.
    </p>
    <p
      v-if="rows.length === 0"
      class="text-body-2 text-medium-emphasis text-center py-4"
    >
      Sin turnos agendados en este mes.
    </p>
    <v-card
      v-for="row in rows"
      :key="row.career"
      :elevation="1"
      class="pa-3 mb-3"
    >
      <div class="d-flex align-center justify-space-between flex-wrap ga-1 mb-2">
        <span
          class="font-weight-bold"
          :class="`text-${getCareerColor(row.career)}`"
        >
          {{ row.humanCareer }}
        </span>
        <span
          class="text-caption font-weight-bold"
          :class="`text-${progressColor(row)}`"
        >
          {{ percentDone(row) }}% Realizado
        </span>
      </div>
      <v-progress-linear
        :model-value="percentDone(row)"
        :color="progressColor(row)"
        height="8"
        rounded
      />
      <div class="d-flex justify-space-between mt-1">
        <span class="text-caption text-medium-emphasis">Agendados: {{ row.planned }}</span>
        <span class="text-caption text-medium-emphasis">Realizados: {{ row.actual }}</span>
      </div>
    </v-card>
    <p class="text-caption text-medium-emphasis mb-0 mt-auto">
      <v-icon
        icon="mdi-clock-outline"
        size="x-small"
      />
      Meta de trazabilidad operativa: >95% de atenciones.
    </p>
  </v-card>
</template>
